import React, { MouseEvent, PropsWithChildren, useEffect, useState } from "react";
import { TApiLab } from "../../../lib/types/labs";
import { useLabs } from "../../../services/laboratories";
import { TGenericGUIDName } from "../../../lib/types/general";
import { useRouteNavigation } from "../../../lib/common/routes";
import { useFileManagement } from "../../../services/file-management";
import { useUserService } from "../../../services/users";
import { ASSETS_ROUTES, LAB_SEARCH_PAGINATION_SIZE } from "../../../lib/common/constants";
import { Pagination } from "../../common/pagination";
import { Spinner } from "../../common/spinner";
import { useNotificationContext } from "../../../contexts/notification-context";
import { phoneFormatHandler } from "../../../lib/common/validation";
import { MessageBtn } from "../../common/message-button";

type TLabRowProps = {
    info: TApiLab;
    regionList: TGenericGUIDName[];
    removeContactCallback: () => void;
};

type TProfileLinkProps = PropsWithChildren<{
    className?: string;
}>;

export const DashboardLabRow = ({ info, regionList, removeContactCallback }: TLabRowProps) => {
    const { navigateRouteClickHandler, getRoute } = useRouteNavigation();
    const { getImagePath } = useFileManagement();
    const { removeFromMyContactList } = useUserService();
    const ProfileLink = ({ children, className }: TProfileLinkProps) => (
        <a
            className={className}
            href={getRoute("dentalLabInfo", info.id.toString())}
            onClick={(e) => navigateRouteClickHandler("dentalLabInfo", e, info.id.toString(), true)}
        >
            {children}
        </a>
    );

    const imgUrl = info?.images?.logo ? getImagePath(info.images.logo, "fixed", info.guid) : ASSETS_ROUTES.IMGS.labAvatarUrl;

    const deleteContactClickHandler = async (e: MouseEvent, contactUserId: number) => {
        e.preventDefault();
        e.stopPropagation();
        await removeFromMyContactList(contactUserId);
        removeContactCallback();
    };

    return (
        <div className="list-group-item list-group-item-action border border-light py-4">
            <div className="row text-center gap-3">
                <div className="col-md-3 ps-4 d-flex justify-content-center justify-content-md-start align-items-center">
                    <ProfileLink>
                        <img className="rounded-circle list-logo list-logo_lab " alt="laboratory logo" src={imgUrl} />
                    </ProfileLink>
                </div>
                <div className="col-md-5 text-center text-md-start ">
                    <p className="employer-name">
                        <ProfileLink className="btn-link">
                            <strong>{info.name}</strong>
                        </ProfileLink>
                        <small>{info.business_type_name}</small>
                    </p>
                    <small>
                        {info.other.specialty && <p>{info.other?.specialty}</p>}
                        {info.other.desiredRegion && (
                            <p>
                                Region:{" "}
                                {regionList
                                    .filter((r) => info?.other?.desiredRegion?.includes(r.guid))
                                    .map((region) => region.name)
                                    .join(", ")}
                            </p>
                        )}
                        <p>
                            {info.other?.location} {info.other?.zip}
                        </p>
                        {info.other.phone && <p> Phone Number: {phoneFormatHandler(info.other?.phone)}</p>}
                    </small>
                </div>
                <div className="col-md-3 mt-4 mt-md-0">
                    <MessageBtn userInfo={info} className="btn-secondary w-75 mb-2 pe-4 " />
                    <ProfileLink className="btn btn-primary mb-2 w-75 pe-4">
                        <i className="fa-regular fa-user me-2"></i> View
                    </ProfileLink>
                    <button onClick={(e) => deleteContactClickHandler(e, info.id)} className="btn btn-outline-danger w-75">
                        <i className="fa-regular fa-trash-can me-2"></i>
                        Delete
                    </button>
                </div>
            </div>
            {/* <hr /> */}
        </div>
    );
};

export const LabList = ({ refLabList }: { refLabList?: React.RefObject<any> }) => {
    const { state, getLabsFromContactList, regions } = useLabs();

    const [labs, setLabs] = useState<TApiLab[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);

    const { setActionsHandler } = useNotificationContext();

    const refreshLabs = (offset: number) => {
        getLabsFromContactList(offset).then((results) => {
            setLabs(results.labs);
            setTotalCount(results.totalCount);
            setPageNumber(offset + 1);
        });
    };

    useEffect(() => {
        setActionsHandler("refreshContactList", () => refreshLabs(0));
        refreshLabs(0);

        // eslint-disable-next-line
    }, []);

    return (
        <div className="list-group">
            {state === "working" && (
                <div className="text-center">
                    <Spinner spinnerColor="text-primary" />
                </div>
            )}
            {state === "idle" && labs.length === 0 && <p className="p-3"> You don't have any contacts yet.</p>}
            {state !== "working" && labs.length > 0 && (
                <>
                    {labs.map((lab) => (
                        <DashboardLabRow key={lab.id} info={lab} removeContactCallback={() => refreshLabs(0)} regionList={regions} />
                    ))}
                    <div className="mt-4">
                        {
                            <Pagination
                                currentPage={pageNumber}
                                totalCount={totalCount}
                                itemsPerPage={LAB_SEARCH_PAGINATION_SIZE}
                                refObject={refLabList}
                                onPageChange={(offset) => {
                                    refreshLabs(offset);
                                }}
                            />
                        }
                    </div>
                </>
            )}
        </div>
    );
};
